import { Injectable } from '@angular/core';
import {
  mapProductsByReferenceOrder,
  mapSearchSortToListGroupingType,
  ProductListApiService,
  ProductListProductsApiService,
} from '@epicuro-next/data/engagement/engagement-api';
import { ProductService } from '@epicuro-next/data/product';
import {
  isCommercialList,
  isNormalList,
  isPowerList,
} from '@epicuro-next/features/engagement/list-utils';
import { MediaType, ProductReference } from '@epicuro-next/models/engagement';
import { selectCommercialListProductReferencesFactory } from '@epicuro-next/state/list-product-reference-entities';
import {
  selectProductEntities,
  upsertProducts,
} from '@epicuro-next/state/product-entities';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { prefetchImages } from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  catchError,
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  changeListGroupingTypeInListEntitySuccess,
  loadList,
  loadListAllProducts,
  loadNextListProducts,
  pushLoadedProductRefsToList,
  pushNewListProductsToListEntity,
  resetListEntities,
  setSearchSortTypeInListEntity,
  upsertListEntity,
} from './list-entities.actions';
import {
  selectIsOwnerOfListEntity,
  selectListEntityFactory,
  selectListEntitySearchSortType,
} from './list-entities.selectors';
import {
  allProductsLoaded,
  areQueriesSame,
  getNotLoadedCommercialListProductReferences,
  getProductsQuery,
  LIST_PRODUCT_QUERY_STEP,
  newQueryForNextProductsAction,
} from './utils';

@Injectable()
export class ListEntitiesEffects {
  public session$ = combineLatest([
    this.store.pipe(select(selectAccountId), filter(isNonNullish)),
    this.store.pipe(select(selectSiteId), filter(isNonNullish)),
  ]);

  constructor(
    private actions$: Actions,
    private productListProductsService: ProductListProductsApiService,
    private productService: ProductService,
    private productListApi: ProductListApiService,
    private store: Store,
  ) {}

  public loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadList),
      concatLatestFrom(() => this.session$),
      mergeMap(([{ listId }, [accountId, siteId]]) =>
        this.productListApi
          .get({ productListId: listId, accountId, siteId })
          .pipe(
            map((list) =>
              upsertListEntity({
                listEntity: {
                  listId,
                  list,
                  loading: false,
                  loadingProducts: false,
                  error: undefined,
                  reorderingCount: 0,
                },
              }),
            ),
            catchError((err) => [
              upsertListEntity({
                listEntity: {
                  listId,
                  list: undefined,
                  loading: false,
                  loadingProducts: false,
                  error: err.toString(),
                  reorderingCount: 0,
                },
              }),
            ]),
          ),
      ),
    ),
  );

  public pushNewListProductsToProductEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pushNewListProductsToListEntity),
      map(({ products }) => upsertProducts({ products })),
    ),
  );

  private loadProductActionWithEntity$ = this.actions$.pipe(
    ofType(loadNextListProducts),
    delayWhen(({ listId }) =>
      this.store.pipe(
        select(selectListEntityFactory(listId)),
        filter((listEntity) => !listEntity?.loading),
      ),
    ),
    mergeMap((action) =>
      this.store.pipe(
        select(selectListEntityFactory(action.listId)),
        filter(isNonNullish),
        take(1),
        map((entity) => ({ entity, action })),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public loadNextListProductsForRegularLists$ = createEffect(() =>
    this.loadProductActionWithEntity$.pipe(
      filter(
        ({ entity }) =>
          !!entity?.list &&
          (isNormalList(entity.list) || isPowerList(entity.list)),
      ),
      concatLatestFrom(({ entity }) => [
        this.session$,
        this.store.pipe(
          select(selectListEntitySearchSortType(entity.list?.id ?? '')),
        ),
      ]),
      switchMap(([{ entity, action }, [accountId, siteId], sortType]) => {
        const query = newQueryForNextProductsAction(action, entity);
        const sameQueries = areQueriesSame(query, entity?.query);

        if (entity && sameQueries && allProductsLoaded(entity)) {
          return of(
            upsertListEntity({
              listEntity: { ...entity, loadingProducts: false },
            }),
          );
        }

        const productsQuery = getProductsQuery(
          query,
          accountId,
          siteId,
          entity?.list?.productsReference,
          sortType,
        );
        return this.productListProductsService
          .getProducts({ query: productsQuery })
          .pipe(
            switchMap((result) => {
              query.start =
                (query.start ?? 0) + (query.num ?? LIST_PRODUCT_QUERY_STEP);

              return [
                upsertProducts({
                  products: (result.products ?? []).filter(
                    isNonNullish,
                  ) as Product[],
                }),
                pushLoadedProductRefsToList({
                  listId: action.listId,
                  productIds: (result.products ?? [])
                    .map((product) => product.productId)
                    .filter(isNonNullish),
                  count: result.resultCount,
                  query,
                }),
              ];
            }),
          );
      }),
    ),
  );

  public loadNextListProductsForCommercialLists$ = createEffect(() =>
    this.loadProductActionWithEntity$.pipe(
      filter(({ entity }) => !!entity?.list && isCommercialList(entity.list)),
      switchMap(({ entity, action }) =>
        this.selectLoadedCommercialListProductReferencesOnce(
          action.listId,
        ).pipe(
          map((references) => {
            const { productListId, sortField } = newQueryForNextProductsAction(
              action,
              entity,
            );

            const query = { productListId, sortField };

            return { entity, action, references, query };
          }),
        ),
      ),
      switchMap(({ entity, references, query }) => {
        const page = 10;
        const { idsToLoad, total } =
          getNotLoadedCommercialListProductReferences(entity, references);

        const pagedIds = idsToLoad.slice(0, page);

        return idsToLoad.length > 0
          ? this.loadProductsWithReferences(pagedIds).pipe(
              switchMap((products) => [
                upsertProducts({ products: products.filter(isNonNullish) }),
                pushLoadedProductRefsToList({
                  listId: entity.listId,
                  productIds: pagedIds.map((e) => e.productId),
                  count: total,
                  query,
                }),
              ]),
              catchError(() =>
                of(
                  pushLoadedProductRefsToList({
                    listId: entity.listId,
                    productIds: pagedIds.map((e) => e.productId),
                    count: total,
                  }),
                ),
              ),
            )
          : of(
              upsertListEntity({
                listEntity: {
                  ...entity,
                  loadingProducts: false,
                  count: total,
                  query,
                },
              }),
            );
      }),
    ),
  );

  // used mostly for fancy list
  // split products to chunks and do multiple requests
  public loadListAllProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListAllProducts),
      switchMap(({ listId }) => {
        return this.store.pipe(
          select(selectListEntityFactory(listId)),
          filter(isNonNullish),
          take(1),
        );
      }),
      concatLatestFrom(() => [
        this.session$,
        this.store.pipe(select(selectProductEntities)),
      ]),
      switchMap(([entity, [_, siteId], productEntities]) => {
        const existingProducts = entity?.list?.availableReferences
          ?.map(({ productId }) => productEntities?.[productId])
          .filter(isNonNullish);
        const itemsToLoad = entity?.list?.availableReferences?.filter(
          ({ productId }) => !productEntities?.[productId],
        );

        return this.productListProductsService
          .getAllProducts({
            site: siteId,
            productReferences: itemsToLoad,
          })
          .pipe(
            switchMap((result) => {
              const products = result.products.concat(existingProducts ?? []);
              const sortedProducts = mapProductsByReferenceOrder(
                products,
                entity?.list?.productsReference ?? [],
              );
              return [
                upsertProducts({
                  products: sortedProducts as Product[],
                }),
                pushLoadedProductRefsToList({
                  listId: entity.listId,
                  productIds: sortedProducts.map(
                    (product) => product.productId,
                  ),
                  count: sortedProducts.length,
                  query: {
                    start: sortedProducts.length,
                    num: sortedProducts.length,
                    productListId: entity.listId,
                  },
                }),
              ];
            }),
          );
      }),
    ),
  );

  public saveGroupingTypeByListOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSearchSortTypeInListEntity),
      concatLatestFrom(({ listId }) =>
        this.store.pipe(select(selectIsOwnerOfListEntity(listId))),
      ),
      filter(([_, isOwner]) => !!isOwner),
      map(([{ listId, sortType }]) => ({
        listId,
        groupingType: mapSearchSortToListGroupingType(sortType),
      })),
      switchMap(({ listId, groupingType }) => {
        return this.productListApi
          .changeGroupingType(listId, groupingType)
          .pipe(
            map(() =>
              changeListGroupingTypeInListEntitySuccess({
                listId,
                groupingType,
              }),
            ),
            catchError(() => EMPTY),
          );
      }),
    ),
  );

  public prefetchListMedia$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(upsertListEntity),
        map((entity) => entity.listEntity.list?.mainMedia),
        filter(isNonNullish),
        filter((media) => media.mediaType === MediaType.Image),
        tap((media) => {
          prefetchImages([media.url]);
        }),
      ),
    { dispatch: false },
  );

  public resetListEntities$ = createEffect(() =>
    combineLatest([
      this.store.pipe(select(selectAccountId)),
      this.store.pipe(select(selectSiteId)),
    ]).pipe(
      map(([accountId, siteId]) => `${accountId}_${siteId}`),
      distinctUntilChanged(),
      map(() => resetListEntities()),
    ),
  );

  private selectLoadedCommercialListProductReferencesOnce(id: string) {
    return this.store.pipe(
      select(selectCommercialListProductReferencesFactory(id)),
      filter(isNonNullish),
      filter((references) => !references.loading),
      take(1),
    );
  }

  private loadProductsWithReferences(productReferences: ProductReference[]) {
    return combineLatest([
      this.store.pipe(select(selectSiteId), filter(isNonNullish)),
    ]).pipe(
      take(1),
      switchMap(([site]) =>
        this.productService.productsByReferences({
          site,
          productReferences: productReferences.map((ref) => ({
            ...ref,
            brandId: ref.brandId ?? site,
          })),
        }),
      ),
      map((resp) => (resp.products ?? []) as Product[]),
    );
  }
}
